import { useCoreServices } from '@bangle.io/context';
import { useAtomValue } from 'jotai';
import React from 'react';
import {
  customPropertyKeys,
  type FrontmatterProperties,
  parseFrontmatterYaml,
  readCreatedBy,
  readPropertyTypes,
  serializeFrontmatterYaml,
  withPropertyTypes,
} from './frontmatter-yaml';
import type { PropertyType } from './property-values';

export type NoteFileTimes = {
  ctime: number | undefined;
  mtime: number | undefined;
};

/**
 * Live frontmatter map for the open note, plus file timestamps for the
 * permanent Created / Last Edited rows. Writes go through the editor engine
 * so the existing save queue persists them with the note body.
 */
export function useNoteProperties(wsPath: string | undefined) {
  const { editorEngine, fileSystem } = useCoreServices();
  const active = useAtomValue(editorEngine.$activeFrontmatter);
  const [fileTimes, setFileTimes] = React.useState<NoteFileTimes>({
    ctime: undefined,
    mtime: undefined,
  });

  const text =
    wsPath && active?.wsPath === wsPath ? (active.text ?? '') : undefined;
  const parsed = React.useMemo(
    () => parseFrontmatterYaml(text ?? ''),
    [text],
  );

  const properties = parsed.properties;
  const types = React.useMemo(
    () => readPropertyTypes(properties),
    [properties],
  );
  const customKeys = React.useMemo(
    () => customPropertyKeys(properties),
    [properties],
  );
  const createdBy = React.useMemo(
    () => readCreatedBy(properties),
    [properties],
  );

  React.useEffect(() => {
    if (!wsPath) {
      setFileTimes({ ctime: undefined, mtime: undefined });
      return;
    }
    let cancelled = false;
    void fileSystem
      .fileStat(wsPath)
      .then((stat) => {
        if (!cancelled) {
          setFileTimes({ ctime: stat.ctime, mtime: stat.mtime });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setFileTimes({ ctime: undefined, mtime: undefined });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [fileSystem, wsPath]);

  // Refresh Last Edited when a save settles for this note.
  React.useEffect(() => {
    if (!wsPath) {
      return;
    }
    return editorEngine.subscribeToSaveStatus(() => {
      const status = editorEngine.getSaveStatus(wsPath);
      if (status.status !== 'clean') {
        return;
      }
      void fileSystem
        .fileStat(wsPath)
        .then((stat) => {
          setFileTimes({ ctime: stat.ctime, mtime: stat.mtime });
        })
        .catch(() => undefined);
    }, wsPath);
  }, [editorEngine, fileSystem, wsPath]);

  const commitProperties = React.useCallback(
    (
      next: FrontmatterProperties,
      nextTypes?: Record<string, PropertyType>,
    ): boolean => {
      if (!wsPath) {
        return false;
      }
      const withTypes = withPropertyTypes(
        next,
        nextTypes ?? readPropertyTypes(next),
      );
      // Drop empty property_types maps via withPropertyTypes already.
      const yaml = serializeFrontmatterYaml(withTypes);
      return editorEngine.setFrontmatterText(wsPath, yaml);
    },
    [editorEngine, wsPath],
  );

  return {
    properties,
    types,
    customKeys,
    createdBy,
    fileTimes,
    parseError: parsed.ok ? undefined : parsed.error,
    ready: Boolean(wsPath && active?.wsPath === wsPath),
    commitProperties,
  };
}
